import React, {
  useContext,
  useEffect,
  useCallback,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  Search,
  Plus,
  Check,
  Clock,
  CheckCheck,
  Users,
  UserRound,
  Building2,
  Star,
  AlarmClock,
  Eye,
  EyeOff,
  ArrowDownAZ,
  ArrowDownWideNarrow,
  Filter,
  FilterX,
  X,
  ListChecks,
  UsersRound,
  ChevronDown,
  Inbox,
  CheckSquare,
  SlidersHorizontal,
  Bookmark,
  Trash2,
  UserPlus,
  MessageSquareText,
} from "lucide-react";
import { toast } from "react-toastify";

import useSomDeNotificacao from "../../hooks/useSomDeNotificacao";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Popover, PopoverContent, PopoverTrigger } from "../ui/popover";
import { cn } from "../../lib/utils";

import NewTicketModal from "../NewTicketModal";
import TicketsList from "../TicketsList";
import { i18n } from "../../translate/i18n";
import { AuthContext } from "../../context/Auth/AuthContext";
import { Can } from "../Can";
import useWhatsApps from "../../hooks/useWhatsApps";
import useLayoutTickets from "../../hooks/useLayoutTickets";
import usePrefTickets from "../../hooks/usePrefTickets";
import {
  COLUNAS_PAINEL,
  IDS_COLUNAS_PAINEL,
} from "../TicketListItem/colunasPainel";
import { IDS_ITENS_DO_CARTAO } from "../TicketListItem/itensDoCartao";
import { IDS_FERRAMENTAS_DA_LISTA } from "./ferramentasDaLista";
import { SeletorDaLista, TrilhoDaLista } from "./NavegacaoDaLista";
import MenuDaLista from "./MenuDaLista";
import PersonalizarLista from "./PersonalizarLista";
import CampoDeBusca from "./CampoDeBusca";
import rules from "../../rules";
import { IDS_BOTOES_DA_LINHA } from "../TicketListItem/botoesDaLinha";
import { ACOES_PADRAO } from "../TicketActionButtons/acoesDaBarra";
import { IDS_BOTOES_DA_CONVERSA } from "../Ticket/botoesDaConversa";
import {
  lerPrefTickets,
  valorFixoPeloCargo,
} from "../../services/prefsTickets";
import { EVENTO_CONTADORES } from "../../services/contadoresDaLista";
import { mostrarAviso } from "../../helpers/avisoDoNavegador";
import { linhasDaListaVisivel } from "../../helpers/linhasDaListaVisivel";
import BarraDeSelecao from "../AcoesEmMassaModal/BarraDeSelecao";
import ResolverTodosModal from "../AcoesEmMassaModal/ResolverTodosModal";
import TransferirTodosModal from "../AcoesEmMassaModal/TransferirTodosModal";
import usePermissions from "../../hooks/usePermissions";
import useQueues from "../../hooks/useQueues";
import useMotivosDeEncerramento from "../../hooks/useMotivosDeEncerramento";
import api from "../../services/api";
import toastError from "../../errors/toastError";

// Botão de ferramenta da lista: só o ícone, com o nome aparecendo embaixo no
// hover — como na referência, onde o rótulo surge quando a mão chega.
const FerramentaDaLista = ({
  icone: Icone,
  rotulo,
  ativo,
  onClick,
  classe,
  // Quantos há do outro lado do botão. `preenchido` desenha o ícone sólido —
  // é o que faz "tenho favoritas" ser visível sem contar o número.
  contagem = 0,
  preenchido = false,
  pulsando = false,
  // Texto lido em voz alta e mostrado no hover longo. Sem ele o nome sai com
  // o número colado num parêntese em cima do outro.
  dica,
}) => (
  <div className="group/ferramenta relative">
    <button
      type="button"
      title={dica}
      aria-label={dica || (contagem > 0 ? `${rotulo} (${contagem})` : rotulo)}
      aria-pressed={ativo}
      onClick={onClick}
      className={cn(
        "relative flex h-8 w-8 items-center justify-center rounded-full transition-colors",
        ativo
          ? "bg-primary/10 text-primary"
          : cn(
              "text-muted-foreground hover:bg-muted hover:text-foreground",
              (preenchido || contagem > 0) && classe,
            ),
      )}
    >
      <Icone
        className={cn(
          "h-[18px] w-[18px]",
          preenchido && "fill-current",
          pulsando && "animate-pulse",
        )}
      />
      {contagem > 0 && (
        <span
          className={cn(
            "absolute -right-0.5 -top-0.5 flex h-4 min-w-4 items-center justify-center rounded-full px-1 text-[9px] font-bold tabular-nums text-primary-foreground",
            pulsando ? "animate-pulse bg-rose-600" : "bg-primary",
          )}
        >
          {contagem > 99 ? "99+" : contagem}
        </span>
      )}
    </button>

    <span className="pointer-events-none absolute left-1/2 top-full z-20 mt-0.5 -translate-x-1/2 whitespace-nowrap rounded bg-foreground px-1.5 py-0.5 text-[10px] font-medium text-background opacity-0 transition-opacity group-hover/ferramenta:opacity-100">
      {rotulo}
    </span>
  </div>
);

// Filtro de várias escolhas. Encapsula o padrão "rótulo + quantos escolhidos
// + lista com caixas de seleção", que se repete nos três filtros.
// O painel de um filtro nunca pode pousar sobre o gatilho de outro: era isso
// que deixava o "Atendentes" inalcançável e fazia o clique destinado a ele
// marcar uma etiqueta do filtro de cima. Pôr os botões lado a lado não
// bastou: com quatro filtros eles ocupam duas linhas, e o painel da linha de
// cima cobria a de baixo.
//
// Então o painel desce até a base da gaveta. Quem faz isso é o `sideOffset`,
// medido na hora de abrir: a distância entre o rodapé do gatilho e o rodapé
// da gaveta. O `PopoverAnchor` seria o caminho natural, mas ancorado num
// elemento vazio o Radix caiu no canto da janela (0,0) e o painel foi parar
// sobre o menu lateral — medir o deslocamento é o que se comporta igual em
// todo tamanho de tela.
//
// `avoidCollisions={false}` impede o Radix de virar o painel para cima quando
// falta altura embaixo: virado, ele cobriria os gatilhos de novo. Pouca altura
// já é resolvida pela rolagem interna do próprio painel.
//
// Um de cada vez, também: dois painéis abertos deixavam o esquecido recebendo
// clique.
const FiltroDeLista = ({
  rotulo,
  opcoes,
  selecionados,
  onChange,
  erro,
  aoTentarDeNovo,
  aberto,
  aoAlternar,
  refDaGaveta,
}) => {
  const refDoGatilho = useRef(null);
  const [deslocamento, setDeslocamento] = useState(4);
  // Sem virar para cima e sem vazar por baixo: o painel é limitado ao que
  // ainda há de janela abaixo da gaveta, e rola por dentro no que não couber.
  const [alturaMaxima, setAlturaMaxima] = useState(288);
  // Painel do filtro da direita não pode sair pela borda: no celular ele
  // passava 64 px da tela e cortava o nome da conexão. Como o
  // `avoidCollisions` está desligado (senão o painel vira para cima e cobre os
  // gatilhos de novo), quem escolhe o lado é esta medida.
  const [alinhamento, setAlinhamento] = useState("start");

  const medir = () => {
    const gatilho = refDoGatilho.current?.getBoundingClientRect();
    const gaveta = refDaGaveta?.current?.getBoundingClientRect();

    if (!gatilho || !gaveta) return;

    setDeslocamento(Math.max(4, gaveta.bottom - gatilho.bottom + 4));
    setAlturaMaxima(
      Math.max(120, Math.min(288, window.innerHeight - gaveta.bottom - 12)),
    );
    // 256 px é a largura do painel (w-64); 8 px de folga na borda.
    setAlinhamento(
      gatilho.left + 256 > window.innerWidth - 8 ? "end" : "start",
    );
  };

  // Redimensionar a janela com o painel aberto deixaria a medida velha.
  useEffect(() => {
    if (!aberto) return undefined;

    window.addEventListener("resize", medir);
    return () => window.removeEventListener("resize", medir);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [aberto]);

  const abrirFechar = (abrir) => {
    if (abrir) medir();

    aoAlternar(abrir);
  };
  const alternar = (id) =>
    onChange(
      selecionados.includes(id)
        ? selecionados.filter((s) => s !== id)
        : [...selecionados, id],
    );

  return (
    <div className="min-w-[45%] flex-1">
    <Popover open={aberto} onOpenChange={abrirFechar}>
      <PopoverTrigger asChild>
        <button
          ref={refDoGatilho}
          type="button"
          className="flex h-8 w-full items-center gap-1.5 rounded-md border px-2.5 text-xs text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
        >
          {rotulo}
          {selecionados.length > 0 && (
            <span className="flex h-4 min-w-4 items-center justify-center rounded-full bg-primary px-1 text-[10px] font-semibold tabular-nums text-primary-foreground">
              {selecionados.length}
            </span>
          )}
          <ChevronDown className="ml-auto h-3.5 w-3.5" />
        </button>
      </PopoverTrigger>

      <PopoverContent
        side="bottom"
        align={alinhamento}
        sideOffset={deslocamento}
        avoidCollisions={false}
        style={{ maxHeight: alturaMaxima }}
        className="scrollbar-discreta w-64 overflow-y-auto p-1"
      >
        {erro ? (
          // "Nada para filtrar." aqui era mentira: as opções existem, o que
          // falhou foi buscá-las — e a pessoa filtrava achando que a empresa
          // não tinha etiqueta nenhuma.
          <div className="space-y-1 p-3">
            <p className="text-xs text-muted-foreground">
              Não foi possível carregar as opções.
            </p>
            <button
              type="button"
              onClick={aoTentarDeNovo}
              className="text-xs font-medium text-primary underline underline-offset-2"
            >
              Tentar de novo
            </button>
          </div>
        ) : (opcoes || []).length === 0 ? (
          <p className="p-3 text-xs text-muted-foreground">
            Nada para filtrar.
          </p>
        ) : (
          opcoes.map((o) => (
            <button
              key={o.id}
              type="button"
              onClick={() => alternar(o.id)}
              className="flex w-full items-center gap-2 rounded-sm px-2 py-1.5 text-left text-xs hover:bg-muted"
            >
              <span
                className={cn(
                  "flex h-4 w-4 shrink-0 items-center justify-center rounded border",
                  selecionados.includes(o.id)
                    ? "border-primary bg-primary text-primary-foreground"
                    : "border-input",
                )}
              >
                {selecionados.includes(o.id) && <Check className="h-3 w-3" />}
              </span>
              {o.color && (
                <span
                  className="h-2.5 w-2.5 shrink-0 rounded-full"
                  style={{ backgroundColor: o.color }}
                />
              )}
              <span className="truncate">{o.name}</span>
            </button>
          ))
        )}
      </PopoverContent>
    </Popover>
    </div>
  );
};

// As abas deixaram de ser por SITUAÇÃO (open/pending) e passaram a ser por
// DONO: "é meu", "é do meu setor", "não é de ninguém ainda". É a pergunta que
// quem atende realmente faz ao abrir a lista — "Atendendo" respondia
// "alguém está atendendo", que não ajuda a decidir o que fazer agora.
//
// Resolvidos continua fora das abas: é o painel "Fechados", alcançado pelo
// botão da barra de ferramentas. Grupos segue como aba própria porque grupo
// não tem dono nem entra no eixo meu/do setor — ficaria eternamente em
// "Aguardando", empurrando conversa de gente de verdade para baixo.
const ESTADOS = [
  {
    value: "minhas",
    rotulo: "Minhas",
    icone: UserRound,
    // Recorte no servidor: só o que está na mão de quem entrou.
    visao: "minhas",
    status: "open",
    ajuda: "Atendimentos que estão com você",
    vazio: {
      titulo: "Nada na sua mão",
      texto: "Aceite uma conversa em Aguardando para começar.",
    },
  },
  {
    value: "setor",
    rotulo: "Nossas",
    icone: Building2,
    // Tudo que está nas filas do usuário, inclusive com outro atendente —
    // é assim de propósito: o colega precisa poder puxar a conversa.
    visao: "setor",
    status: "open",
    ajuda: "Atendimentos dos seus departamentos, inclusive os de colegas",
    vazio: {
      titulo: "Nenhum atendimento nos seus departamentos",
      texto: "As conversas em andamento dos seus departamentos aparecem aqui.",
    },
  },
  {
    value: "pending",
    rotulo: "Aguardando",
    icone: Clock,
    status: "pending",
    ajuda: "Ninguém assumiu ainda",
    vazio: {
      titulo: "Nenhum cliente esperando",
      texto: "Novas conversas aparecem aqui assim que chegam.",
    },
  },
  {
    value: "grupos",
    rotulo: "Grupos",
    icone: Users,
    // Grupo não é "aceito" por ninguém: fica em aguardando para sempre. A
    // aba lista as duas situações, senão nunca mostra nada.
    status: "open,pending",
    ajuda: "Conversas em grupos (WhatsApp e Telegram)",
    somenteGrupos: true,
    vazio: {
      titulo: "Nenhuma conversa em grupo",
      texto: "Grupos aparecem aqui quando alguém escreve neles.",
    },
  },
];

// Devolve sempre a MESMA referência enquanto o conteúdo não mudar. Array novo
// com o mesmo conteúdo é array novo para o React, e estes descem como
// dependência de efeito no TicketsList — onde identidade nova significa
// "filtro mudou": lista zerada e busca refeita.
const useListaEstavel = (lista) => {
  const chave = JSON.stringify(lista);

  // A dependência é o CONTEÚDO, de propósito: é exatamente a comparação que
  // o React não faz sozinho.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  return useMemo(() => lista, [chave]);
};

// Id do item "Sem departamento" no filtro de departamentos. Texto, para não
// colidir com id de fila.
const SEM_DEPARTAMENTO = "sem";

// O filtro de departamentos em duas partes: as filas marcadas e o "sem
// departamento". Preferência (ou visão) de antes da gaveta não tem a segunda:
// naquele seletor o sem fila vinha sempre junto, e todas marcadas era o mesmo
// que nenhuma. É assim que ela é lida, para ninguém perder de vista os
// atendimentos sem fila no dia da troca.
const lerFiltroDeFilas = (filas, semFila, disponiveis) => {
  const validas = (filas || []).filter((id) => disponiveis.includes(id));

  if (typeof semFila === "boolean") return { filas: validas, semFila };

  const todas = validas.length === disponiveis.length;

  return { filas: todas ? [] : validas, semFila: !todas && validas.length > 0 };
};

const RESOLVIDOS = {
  value: "closed",
  rotulo: "Resolvidos",
  icone: CheckCheck,
  status: "closed",
  ajuda: "Atendimentos encerrados",
  vazio: {
    titulo: "Nada resolvido por aqui",
    texto: "Os atendimentos encerrados ficam guardados nesta aba.",
  },
};

const TicketsManager = () => {
  // O layout da lista só se achava em Configurações › Aparência — longe de
  // onde ele muda a tela. Aqui muda na hora: o evento na mesma aba já existe.
  const layout = useLayoutTickets();

  // Dois campos: o filtro de contato (nome/telefone) estreita SÓ a aba em que
  // a pessoa está — as abas continuam na tela, cada uma contando quem bate —,
  // e a busca nas mensagens procura no conteúdo, na aba ou em todas
  // (preferência `escopoBuscaMensagem`). O texto sobrevive à troca de aba.
  // Os dois valem a partir de 2 caracteres: com 1 a busca no conteúdo varre
  // todas as mensagens (o índice trigram não ajuda com menos de 3). É o mesmo
  // mínimo do Ctrl+K e da busca por telefone.
  const [filtroContato, setFiltroContato] = useState("");
  const [buscaMensagem, setBuscaMensagem] = useState("");
  const [campoBuscaMensagem] = usePrefTickets("campoBuscaMensagem", "campo");
  const [escopoBuscaMensagem, setEscopoBuscaMensagem] = usePrefTickets(
    "escopoBuscaMensagem",
    "aba",
  );
  // No modo "icone" o segundo campo só aparece depois do clique — e não some
  // enquanto houver termo: busca valendo sem campo à vista não tem como sair.
  const [buscaMensagemAberta, setBuscaMensagemAberta] = useState(false);
  const campoDaMensagemAberto = buscaMensagemAberta || Boolean(buscaMensagem);
  const filtro =
    filtroContato.trim().length >= 2 ? filtroContato.trim() : undefined;
  const termoMensagem =
    buscaMensagem.trim().length >= 2 ? buscaMensagem.trim() : undefined;
  // Em "todas" o resultado é uma lista só, por cima das abas; em "aba" a
  // busca desce para as listas das abas, e cada uma conta quem achou.
  const buscando = Boolean(termoMensagem) && escopoBuscaMensagem === "todas";
  const termoNaAba = buscando ? undefined : termoMensagem;
  // Situação na busca em todas: sem dizer QUAL situação cada linha tem, o
  // resultado misturado confunde. Os selos na linha resolvem a leitura;
  // estes botões resolvem o recorte.
  const [buscaStatus, setBuscaStatus] = useState("");
  // Quantos resultados há em cada situação — vem da própria busca.
  const [contagemDaBusca, setContagemDaBusca] = useState(null);
  // Abre em "Minhas": o primeiro olhar da pessoa é para o que é dela — até
  // ela escolher outra aba, que é a partir daí a aba dela.
  const [estado, setEstado] = usePrefTickets("aba", "minhas");
  // `null` fechado; "lista" abre a busca de contatos; "novoContato" abre
  // direto o cadastro — os dois caminhos do botão "+".
  const [novoAtendimento, setNovoAtendimento] = useState(null);
  const [showAllTickets, setShowAllTickets] = useState(false);
  // Filtro pessoal: mostra só o que a pessoa marcou com a estrela. Vale
  // dentro da aba em que ela está, não substitui a aba.
  const [soFavoritas, setSoFavoritas] = usePrefTickets("soFavoritas", false);
  // Adiado some das listas normais. Sem este filtro, o atendimento adormecido
  // não teria caminho de volta antes da hora marcada.
  const [soAdiados, setSoAdiados] = usePrefTickets("soAdiados", false);
  // Período da aba "Resolvidos". Sem recorte ela cresce para sempre, e o que
  // interessa — o que fechou hoje — some no meio de meses de histórico.
  // Uma semana cobre o uso real: conferir o que a equipe encerrou.
  const [periodoResolvidos, setPeriodoResolvidos] = usePrefTickets(
    "periodoResolvidos",
    "7",
  );
  const searchInputRef = useRef();
  const { user } = useContext(AuthContext);
  const { temPermissao, escopoDeTickets } = usePermissions();
  // O FiltroDeLista fala em { id, name }; o catálogo de motivos, em
  // { chave, rotulo }. A tradução mora aqui em vez de num segundo catálogo.
  const { todos: motivosDeEncerramento } = useMotivosDeEncerramento();
  const motivosParaFiltro = useMemo(
    () => motivosDeEncerramento.map((m) => ({ id: m.chave, name: m.rotulo })),
    [motivosDeEncerramento],
  );

  const [contagens, setContagens] = useState({
    minhas: 0,
    setor: 0,
    pending: 0,
    grupos: 0,
  });

  // Avisos do prazo de pausa vencido. Cada um por conta própria: piscar é
  // discreto e fica ligado; som e aviso do navegador interrompem, então quem
  // quiser liga em Configurações › Aparência.
  const [avisoPausaPiscar] = usePrefTickets("avisoPausaPiscar", true);
  const [avisoPausaSom] = usePrefTickets("avisoPausaSom", false);
  const [avisoPausaNotificacao] = usePrefTickets(
    "avisoPausaNotificacao",
    false,
  );

  // Quem enxerga a empresa inteira filtra pela empresa inteira.
  //
  // Mesma condição do backend (`filasQuePodePedir`): escopo "company" ou
  // perfil admin. Enquanto o seletor listava só `user.queues`, um supervisor
  // sem vínculo de fila mandava lista vazia, o servidor devolvia "as minhas"
  // (nenhuma) e a lista virava só "sem fila" — o pendente sumia no instante
  // em que ganhava uma fila, e a fila de destino nem era oferecida na barra
  // para ele pedir de volta.
  const veEmpresaInteira =
    escopoDeTickets === "company" || user?.profile === "admin";
  const { findAll: listarFilas } = useQueues();
  const [filasDaEmpresa, setFilasDaEmpresa] = useState(null);

  useEffect(() => {
    if (!veEmpresaInteira) return undefined;

    let vivo = true;

    // `/queue` é leitura livre para qualquer autenticado e devolve só as filas
    // da empresa de quem pede — nenhuma fila de outro inquilino entra aqui.
    listarFilas()
      .then((filas) => {
        if (vivo) setFilasDaEmpresa(filas || []);
      })
      // Falhou a busca: fica com as filas do usuário, que é o comportamento
      // de sempre. Filtro mais estreito é ruim; tela quebrada é pior.
      .catch(() => {});

    return () => {
      vivo = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [veEmpresaInteira]);

  // As filas que a barra oferece — e o padrão quando nada está marcado.
  const filasDisponiveis =
    veEmpresaInteira && filasDaEmpresa?.length ? filasDaEmpresa : user.queues;
  const idsDisponiveis = useMemo(
    () => (filasDisponiveis || []).map((q) => q.id),
    [filasDisponiveis],
  );
  // Filas escolhidas: a preferência é filtrada pelas filas que a pessoa PODE
  // pedir hoje. Sem isso, quem perdeu acesso a uma fila continuaria pedindo os
  // atendimentos dela — e uma preferência antiga viraria filtro que o servidor
  // recusa. Escolha que sobrou vazia volta a ser "todas as disponíveis".
  //
  // A peneira é feita no cálculo, não num efeito que corrige o estado depois:
  // as filas da empresa chegam DEPOIS da primeira renderização, e um estado
  // congelado no primeiro render deixaria o seletor oferecendo filas novas com
  // nenhuma delas marcada.
  const [filasSalvas, setFilasSalvas] = usePrefTickets("filas", null);
  const [filasEscolhidas, setFilasEscolhidas] = useState(filasSalvas);
  const [semFilaSalvo, setSemFilaSalvo] = usePrefTickets("filtroSemFila", null);
  const { filas: filasMarcadas, semFila: semFilaMarcada } = useMemo(
    () => lerFiltroDeFilas(filasEscolhidas, semFilaSalvo, idsDisponiveis),
    [filasEscolhidas, semFilaSalvo, idsDisponiveis],
  );
  // Nada marcado é "todos": as filas disponíveis e o sem fila, como sempre.
  const selectedQueueIds = filasMarcadas.length ? filasMarcadas : idsDisponiveis;
  const semFila = filasMarcadas.length
    ? semFilaMarcada
      ? "incluir"
      : "excluir"
    : semFilaMarcada
      ? "so"
      : undefined;
  const filasNoFiltro = useMemo(
    () => [...filasMarcadas, ...(semFilaMarcada ? [SEM_DEPARTAMENTO] : [])],
    [filasMarcadas, semFilaMarcada],
  );
  const opcoesDeFilas = useMemo(
    () => [
      { id: SEM_DEPARTAMENTO, name: "Sem departamento" },
      ...(filasDisponiveis || []),
    ],
    [filasDisponiveis],
  );
  const escolherFilas = useCallback(
    (ids) => {
      const filas = ids.filter((id) => id !== SEM_DEPARTAMENTO);

      setFilasEscolhidas(filas);
      setFilasSalvas(filas);
      setSemFilaSalvo(ids.includes(SEM_DEPARTAMENTO));
    },
    [setFilasSalvas, setSemFilaSalvo],
  );

  // "abertos" mostra as abas Atendendo/Aguardando/Grupos; "fechados" mostra
  // só os resolvidos, como na referência.
  const [painel, setPainel] = usePrefTickets("painel", "abertos");
  const [filtroTags, setFiltroTags] = usePrefTickets("filtroTags", []);
  const [filtroUsuarios, setFiltroUsuarios] = usePrefTickets(
    "filtroUsuarios",
    [],
  );
  const [filtroConexoes, setFiltroConexoes] = usePrefTickets(
    "filtroConexoes",
    [],
  );

  // Quantos estão marcados com a estrela — o número no botão de favoritas. A
  // rota devolve os ids de quem está logado, que é o mesmo dado que a lista
  // usa para desenhar a estrela cheia.
  const [totalFavoritas, setTotalFavoritas] = useState(0);
  // Quantos estão em pausa e quantos já venceram o prazo. Contar no servidor
  // porque o botão precisa do número mesmo com o filtro desligado.
  const [pausados, setPausados] = useState({
    total: 0,
    vencidos: 0,
    comMensagem: 0,
  });
  const { tocar } = useSomDeNotificacao();
  const tocarRef = useRef(tocar);
  // O que já foi avisado, guardado de duas formas porque uma só erra:
  //
  // - o HORÁRIO até onde já se avisou (só avança) segura o alarme falso quando
  //   a amostra do servidor vem cortada e um vencido antigo reentra nela;
  // - os IDS já vistos pegam o vencido que chegou por transferência — prazo
  //   velho, mas primeira vez que esta pessoa o vê.
  //
  // Os ids só valem com a amostra inteira (`truncada: false`); cortada, manda o
  // horário. `null` = ainda não houve resposta nenhuma, e aí o que existe é
  // passado, não notícia.
  const vencidosAte = useRef(null);
  const vencidosVistos = useRef(new Set());
  // Consulta em voo: o relógio de 60s e o evento dos contadores podem cair
  // juntos, e duas respostas lendo a mesma marca avisariam o mesmo prazo duas
  // vezes (o aviso do navegador tem `tag` e se substitui, o som não).
  const consultandoPausa = useRef(false);
  // Pedido que chegou durante a consulta e ainda precisa acontecer.
  const pausaPendente = useRef(false);
  // A própria consulta, para o reagendamento não entrar no laço de
  // dependências dela mesma.
  const carregarPausadosRef = useRef(null);
  // Lidas por referência: o alerta acontece dentro da consulta, e depender
  // delas ali refaria a consulta a cada mudança de preferência.
  const preferenciasDoAviso = useRef({ som: false, notificacao: false });

  useEffect(() => {
    tocarRef.current = tocar;
  }, [tocar]);

  useEffect(() => {
    preferenciasDoAviso.current = {
      som: avisoPausaSom,
      notificacao: avisoPausaNotificacao,
    };
  }, [avisoPausaSom, avisoPausaNotificacao]);

  const carregarFavoritas = useCallback(async () => {
    try {
      const { data } = await api.get("/ticket-favorites");
      setTotalFavoritas((data?.ticketIds || []).length);
    } catch {
      // Número na barra não vale derrubar a tela de atendimentos.
    }
  }, []);

  // Prazo(s) que acabaram de vencer. Som e aviso do navegador conforme a
  // pessoa escolheu; o piscar do relógio não passa por aqui, é desenho.
  const avisarPrazoVencido = useCallback((quantos) => {
    const { som, notificacao } = preferenciasDoAviso.current;

    if (som) tocarRef.current?.();

    if (notificacao) {
      // `tag` fixa: dois prazos vencendo juntos substituem o mesmo aviso em
      // vez de empilhar duas caixas iguais.
      mostrarAviso(
        "Pausa vencida",
        quantos === 1
          ? "1 atendimento em pausa venceu o prazo."
          : `${quantos} atendimentos em pausa venceram o prazo.`,
        "pausa-vencida",
      );
    }
  }, []);

  const carregarPausados = useCallback(async () => {
    // Duas consultas em voo leriam a mesma marca e avisariam o mesmo prazo
    // duas vezes. A segunda não é descartada, fica marcada: quem pediu foi uma
    // AÇÃO (desligar o relógio de um atendimento), e jogar fora o pedido
    // deixava o botão parado até o próximo minuto, logo depois do clique que
    // devia atualizá-lo.
    if (consultandoPausa.current) {
      pausaPendente.current = true;
      return;
    }

    consultandoPausa.current = true;

    try {
      // O número conta o MESMO recorte que a lista aberta mostra: a aba (ou o
      // painel Fechados, que substitui as abas), as filas marcadas, "Todos",
      // favoritas e os filtros da gaveta. Antes o botão tinha escopo próprio e
      // dizia "3 em pausa" onde o filtro abria 2 — e ninguém conseguia
      // explicar a diferença olhando a tela.
      //
      // Fora ficam a BUSCA (o número piscaria a cada tecla) e o filtro de
      // motivo, que só existe em Resolvidos — onde não há pausa: encerrar
      // apaga o prazo.
      const aba =
        painel === "fechados"
          ? RESOLVIDOS
          : ESTADOS.find((e) => e.value === estado) || ESTADOS[0];

      const { data } = await api.get("/tickets/contagem-pausa", {
        // Sem timeout, uma requisição pendurada deixaria a trava de "consulta
        // em voo" ligada para o resto da sessão e o número congelaria calado.
        timeout: 30000,
        params: {
          visao: aba.visao,
          status: aba.status,
          isGroup: aba.somenteGrupos ? "true" : "false",
          showAll: aba.value === "closed" ? "true" : String(showAllTickets),
          onlyFavorites: String(soFavoritas),
          queueIds: JSON.stringify(selectedQueueIds),
          semFila,
          tagIds: JSON.stringify(filtroTags),
          userIds: JSON.stringify(filtroUsuarios),
          whatsappIds: JSON.stringify(filtroConexoes),
        },
      });
      const amostra = (data?.vencidosEm || [])
        .map((v) => ({ id: v?.id, em: new Date(v?.em).getTime() }))
        .filter((v) => Number.isFinite(v.em));

      setPausados({
        total: Number(data?.total) || 0,
        vencidos: Number(data?.vencidos) || amostra.length,
        comMensagem: Number(data?.comMensagem) || 0,
      });

      const marca = vencidosAte.current;
      const jaVistos = vencidosVistos.current;

      // Só avança, nunca volta: um vencido sumindo da lista (alguém reagendou)
      // não pode fazer os que ficaram parecerem notícia de novo.
      vencidosAte.current = Math.max(marca || 0, ...amostra.map((v) => v.em), 0);

      // Amostra cortada: os ids que faltam não podem entrar no conjunto, senão
      // o que reaparecer depois pareceria novo. Nesse caso o horário decide
      // sozinho, e o conjunto é zerado para não guardar meia verdade.
      vencidosVistos.current = data?.truncada
        ? new Set()
        : new Set(amostra.map((v) => v.id));

      // Primeira resposta (ou troca de escopo): o que está vencido já estava
      // vencido antes de a tela abrir. Avisar aqui seria alarme de ontem.
      if (marca === null) return;

      const novos = amostra.filter(
        (v) => v.em > marca || (!data?.truncada && !jaVistos.has(v.id)),
      ).length;

      if (novos) avisarPrazoVencido(novos);
    } catch {
      // Idem.
    } finally {
      consultandoPausa.current = false;

      if (pausaPendente.current) {
        pausaPendente.current = false;
        carregarPausadosRef.current?.();
      }
    }
  }, [
    estado,
    painel,
    selectedQueueIds,
    semFila,
    showAllTickets,
    soFavoritas,
    filtroTags,
    filtroUsuarios,
    filtroConexoes,
    avisarPrazoVencido,
  ]);

  // A consulta guardada na referência: é dela que sai o reagendamento do
  // pedido que chegou durante outra consulta.
  useEffect(() => {
    carregarPausadosRef.current = carregarPausados;
  }, [carregarPausados]);

  useEffect(() => {
    carregarFavoritas();
  }, [carregarFavoritas]);

  // O prazo vence pela passagem do tempo, sem nenhum evento: sem esta recarga
  // periódica o relógio só cobraria o retorno no próximo F5.
  useEffect(() => {
    // Escopo novo, contagem nova: o que aparecer na primeira resposta dele não
    // é prazo que acabou de vencer, é o recorte mudando.
    vencidosAte.current = null;
    vencidosVistos.current = new Set();
    carregarPausados();

    const id = setInterval(carregarPausados, 60000);

    return () => clearInterval(id);
  }, [carregarPausados]);

  // A estrela e o relógio são clicados na linha, que é outro pedaço da tela.
  useEffect(() => {
    const atualizar = (e) => {
      if (e.detail?.tipo === "favoritos") carregarFavoritas();
      if (e.detail?.tipo === "pausa") carregarPausados();
    };

    window.addEventListener(EVENTO_CONTADORES, atualizar);
    return () => window.removeEventListener(EVENTO_CONTADORES, atualizar);
  }, [carregarFavoritas, carregarPausados]);

  const { whatsApps } = useWhatsApps();

  // A conexão só aparece na linha quando a empresa tem mais de uma. Calculado
  // aqui e descido por prop: cada `useWhatsApps` busca `/whatsapp/` por conta
  // própria, e há uma lista montada por aba.
  const mostrarConexao = (whatsApps || []).length > 1;

  // A aba de grupos só existe se alguma conexão estiver configurada para
  // atendê-los — do contrário é uma aba sempre vazia.
  //
  // `groupAsTicket` entra junto porque é ELE que cria o atendimento de grupo;
  // `showGroups` só decide se a aba aparece. Com um ligado e o outro não, a
  // conversa de grupo virava ticket e não aparecia em lugar nenhum: todas as
  // outras abas filtram por `isGroup=false`, e a aba que mostraria não
  // existia. Mensagem de cliente entrando e sumindo, sem erro em canto algum
  // — apareceu no piloto do Telegram.
  const mostrarGrupos = (whatsApps || []).some(
    (w) => w.showGroups || w.groupAsTicket,
  );
  const abas = ESTADOS.filter((e) => !e.somenteGrupos || mostrarGrupos);

  // Se a aba sumir (conexão desmarcada), volta para uma aba que existe.
  useEffect(() => {
    if (estado === "grupos" && !mostrarGrupos) setEstado("minhas");
  }, [estado, mostrarGrupos]);

  // "Todos" começa desligado para todo mundo, inclusive administrador: a
  // lista abre com os atendimentos de quem entrou, e ver os dos outros é uma
  // escolha de quem clica no botão. Antes o admin caía direto na fila
  // inteira da empresa e não tinha como enxergar só o que era dele.

  const [ordemAZ, setOrdemAZ] = usePrefTickets("ordemAZ", false);
  // A gaveta abre sozinha quando há filtro guardado: filtro invisível é a
  // forma mais rápida de a pessoa achar que a lista está vazia.
  const [filtrosAbertos, setFiltrosAbertos] = useState(
    () =>
      lerPrefTickets("filtroTags", []).length > 0 ||
      lerPrefTickets("filtroUsuarios", []).length > 0 ||
      lerPrefTickets("filtroConexoes", []).length > 0 ||
      lerPrefTickets("filtroMotivos", []).length > 0,
  );
  // Motivo do encerramento. Só vale na aba Resolvidos — nas outras não existe
  // motivo nenhum, e o filtro esvaziaria a lista sem explicar por quê.
  const [filtroMotivos, setFiltroMotivos] = usePrefTickets("filtroMotivos", []);
  const [etiquetas, setEtiquetas] = useState([]);
  const [usuarios, setUsuarios] = useState([]);
  // A falha era silenciosa: os filtros abriam dizendo "Nada para filtrar", que
  // é o texto de quem não tem etiqueta nenhuma cadastrada. Guardando o erro, o
  // popover pode dizer o que houve e oferecer uma nova tentativa.
  const [erroDasOpcoes, setErroDasOpcoes] = useState(false);
  const [catalogoDeUsuariosParcial, setCatalogoDeUsuariosParcial] =
    useState(false);
  // Qual filtro está com o painel aberto. Um de cada vez: com dois abertos, o
  // esquecido continuava recebendo clique.
  const [filtroAberto, setFiltroAberto] = useState(null);
  // O rodapé desta gaveta é a linha até onde os painéis descem.
  const refDaGaveta = useRef(null);
  const [tentativaDasOpcoes, setTentativaDasOpcoes] = useState(0);

  // As opções dos filtros só são buscadas quando os filtros aparecem: quem
  // nunca abre a gaveta não paga por ela.
  useEffect(() => {
    if (!filtrosAbertos || etiquetas.length || usuarios.length) return;

    (async () => {
      try {
        const [t, u] = await Promise.all([api.get("/tags"), api.get("/users")]);
        setEtiquetas(t.data?.tags || t.data || []);
        setUsuarios(u.data?.users || []);
        // /users vem paginado de 20 em 20: com mais atendentes que isso, a
        // primeira página NÃO é o catálogo, e podar o filtro por ela apagaria
        // em silêncio o atendente escolhido que ficou na página 2.
        setCatalogoDeUsuariosParcial(Boolean(u.data?.hasMore));
        setErroDasOpcoes(false);
      } catch (err) {
        // Sem as listas os filtros abrem vazios; não vale derrubar a tela.
        setErroDasOpcoes(true);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filtrosAbertos, tentativaDasOpcoes]);

  const recarregarOpcoes = useCallback(() => {
    setErroDasOpcoes(false);
    setTentativaDasOpcoes((n) => n + 1);
  }, []);
  // Visões salvas: o recorte inteiro da lista com um nome. Quem cobre três
  // filas passa o dia refazendo a mesma combinação de aba, ordem e filtros —
  // salvar uma vez e voltar nela com um clique é a diferença.
  const [visoes, setVisoes] = usePrefTickets("visoes", []);
  // Colunas do Painel que a pessoa quer ver. Não entra em visão salva: é
  // preferência de exibição, não recorte da lista — aplicar a visão de um
  // colega não deve reconfigurar as suas colunas.
  const [colunasVisiveis, setColunasVisiveis] = usePrefTickets(
    "colunasPainel",
    IDS_COLUNAS_PAINEL,
  );
  // O que aparece dentro de cada linha (Clássico e Foco) e quais botões a
  // barra de ferramentas mostra. Tudo ligado por padrão: quem não mexer não
  // vê diferença nenhuma.
  const [itensVisiveis, setItensVisiveis] = usePrefTickets(
    "itensDoCartao",
    IDS_ITENS_DO_CARTAO,
  );
  // A partir de qual volta o selo "Nº atendimento" aparece. 2 = toda
  // reincidência.
  const [recorrenteMinimo] = usePrefTickets("atendimentoRecorrenteMinimo", 2);
  const [ferramentas, setFerramentas] = usePrefTickets(
    "ferramentasDaLista",
    IDS_FERRAMENTAS_DA_LISTA,
  );
  // Botões que a linha mostra no hover. Valem nos três layouts: a linha é a
  // mesma, muda só o desenho dela.
  const [botoesDaLinha, setBotoesDaLinha] = usePrefTickets(
    "botoesDaLinha",
    IDS_BOTOES_DA_LINHA,
  );
  // Atalhos da barra do atendimento e botões da conversa. A tela não os
  // desenha — quem desenha é o TicketActionButtons e o MessageInput —, mas a
  // visão salva os leva junto, e aplicar uma visão precisa poder trocá-los.
  const [acoesBarra, setAcoesBarra] = usePrefTickets("acoesBarra", ACOES_PADRAO);
  const [botoesDaConversa, setBotoesDaConversa] = usePrefTickets(
    "botoesDaConversa",
    IDS_BOTOES_DA_CONVERSA,
  );
  const temFerramenta = (id) => ferramentas.includes(id);

  // Estrutura da lista. Os padrões (PADRAO_TICKETS) são a tela de sempre.
  const [navegacaoLista] = usePrefTickets("navegacaoLista", "abas");
  const [resolvidosComoAba] = usePrefTickets("resolvidosComoAba", false);
  const [ferramentasNoMenu, setFerramentasNoMenu] = usePrefTickets(
    "ferramentasNoMenu",
    [],
  );
  const [densidadeLinha] = usePrefTickets("densidadeLinha", "confortavel");
  const lateral = navegacaoLista === "lateral";
  const seletor = navegacaoLista === "seletor";

  // Três estados por ferramenta: fora de `ferramentasDaLista` fica escondida;
  // dentro e em `ferramentasNoMenu` vai para o menu ⋯; dentro e fora dele, na
  // barra. Na navegação lateral, favoritas e pausa viram pastas do trilho.
  const ferramentaNoTrilho = (id) =>
    lateral && (id === "favoritas" || id === "adiados");
  const naBarra = (id) =>
    temFerramenta(id) &&
    !ferramentasNoMenu.includes(id) &&
    !ferramentaNoTrilho(id);
  const noMenu = (id) =>
    temFerramenta(id) &&
    ferramentasNoMenu.includes(id) &&
    !ferramentaNoTrilho(id);
  // O mesmo teste do <Can> da barra, como valor: o menu monta uma lista.
  const podeVerTodos = Boolean(
    rules[user.profile]?.static?.includes("tickets-manager:showall"),
  );

  // Cada ação em massa depende do PAR botão-escondido + permissão. Testar os
  // dois eixos separados abria o menu vazio para quem, por exemplo, escondeu
  // "Resolver todos" e só tem permissão de resolver.
  const podeResolverTodos =
    naBarra("resolverTodos") && temPermissao("tickets:closeAll");
  const podeTransferirTodos =
    naBarra("transferirTodos") && temPermissao("tickets:transferAll");
  const [nomeDaVisao, setNomeDaVisao] = useState("");

  const filtroAtual = () => ({
    aba: estado,
    painel,
    ordemAZ,
    soFavoritas,
    soAdiados,
    periodoResolvidos,
    filtroTags,
    filtroUsuarios,
    filtroConexoes,
    filtroMotivos,
    filas: filasMarcadas,
    filtroSemFila: semFilaMarcada,
    // A visão é a tela inteira com um nome: o recorte da lista E o que está à
    // mão. Quem salva "Minha manhã" quer os mesmos botões de volta.
    colunasPainel: colunasVisiveis,
    itensDoCartao: itensVisiveis,
    botoesDaLinha,
    ferramentasDaLista: ferramentas,
    ferramentasNoMenu,
    acoesBarra,
    botoesDaConversa,
  });

  const salvarVisao = () => {
    const nome = nomeDaVisao.trim().slice(0, 40);
    if (!nome) return;

    // Mesmo nome sobrescreve: salvar "Minha manhã" de novo é atualizar, não
    // ganhar duas linhas iguais na lista.
    const outras = visoes.filter((v) => v.nome !== nome);
    setVisoes(
      [
        ...outras,
        { id: String(Date.now()), nome, filtro: filtroAtual() },
      ].slice(-20),
    );
    setNomeDaVisao("");
    toast.success(`Visão "${nome}" salva.`);
  };

  const aplicarVisao = (visao) => {
    const f = visao.filtro || {};

    setEstado(f.aba || "minhas");
    setPainel(f.painel || "abertos");
    setOrdemAZ(!!f.ordemAZ);
    setSoFavoritas(!!f.soFavoritas);
    setSoAdiados(!!f.soAdiados);
    setPeriodoResolvidos(f.periodoResolvidos || "7");
    setFiltroTags(f.filtroTags || []);
    setFiltroUsuarios(f.filtroUsuarios || []);
    setFiltroConexoes(f.filtroConexoes || []);
    setFiltroMotivos(f.filtroMotivos || []);

    // Fila que a pessoa perdeu no caminho não volta pela visão.
    const departamentos = lerFiltroDeFilas(
      f.filas,
      f.filtroSemFila,
      idsDisponiveis,
    );
    escolherFilas([
      ...departamentos.filas,
      ...(departamentos.semFila ? [SEM_DEPARTAMENTO] : []),
    ]);

    // Visão salva antes desta mudança não traz o que a tela mostra — e ausente
    // é "não mexa", não "esconda tudo".
    if (f.colunasPainel) setColunasVisiveis(f.colunasPainel);
    if (f.itensDoCartao) setItensVisiveis(f.itensDoCartao);
    if (f.botoesDaLinha) setBotoesDaLinha(f.botoesDaLinha);
    if (f.ferramentasDaLista) setFerramentas(f.ferramentasDaLista);
    if (f.ferramentasNoMenu) setFerramentasNoMenu(f.ferramentasNoMenu);
    if (f.acoesBarra) setAcoesBarra(f.acoesBarra);
    if (f.botoesDaConversa) setBotoesDaConversa(f.botoesDaConversa);

    // A visão traz filtros que a gaveta fechada esconderia — e lista curta
    // sem filtro à vista parece lista vazia.
    if (
      (f.filtroTags || []).length ||
      (f.filtroUsuarios || []).length ||
      (f.filtroConexoes || []).length ||
      (f.filtroMotivos || []).length ||
      departamentos.filas.length ||
      departamentos.semFila
    ) {
      setFiltrosAbertos(true);
    }
  };

  const apagarVisao = (id) => setVisoes(visoes.filter((v) => v.id !== id));

  // Qual visão está em uso agora — o mesmo recorte, campo a campo.
  //
  // Comparar `JSON.stringify` do objeto direto não serve: a visão salva volta
  // do JSONB do Postgres, que NÃO preserva a ordem das chaves. O mesmo recorte
  // gerava textos diferentes e nenhuma visão aparecia como ativa depois do F5.
  //
  // Lista é comparada ORDENADA: marcar e desmarcar o mesmo botão muda a ordem
  // do array sem mudar o recorte, e a visão deixava de aparecer como ativa.
  // Campo que a visão não tem (as salvas antes de a visão levar o que a tela
  // mostra) não conta: vale o valor de agora, dos dois lados.
  const assinaturaDoFiltro = (f = {}) => {
    const agora = filtroAtual();
    // Visão salva antes da gaveta guarda as filas no formato antigo.
    const departamentos = f.filas
      ? lerFiltroDeFilas(f.filas, f.filtroSemFila, idsDisponiveis)
      : {};
    const lido = {
      ...f,
      filas: departamentos.filas,
      filtroSemFila: departamentos.semFila,
    };

    return JSON.stringify(
      Object.keys(agora)
        .sort()
        .map((k) => {
          const v = lido[k] ?? agora[k] ?? null;
          return [k, Array.isArray(v) ? [...v].sort() : v];
        }),
    );
  };

  const atual = assinaturaDoFiltro(filtroAtual());
  const visaoEmUso = visoes.find((v) => assinaturaDoFiltro(v.filtro) === atual);

  // Id de etiqueta, atendente ou conexão que não existe mais some do filtro.
  // Vale para visão antiga e para preferência antiga: o filtro invisível de um
  // registro apagado devolvia lista vazia sem explicação.
  //
  // useMemo não é enfeite aqui: estes arrays descem para o TicketsList, que os
  // usa como dependência do efeito de busca. Um array novo a cada render
  // refazia a busca, que causava outro render — a lista ficava presa no
  // esqueleto para sempre.
  const soConhecidos = (ids, opcoes) =>
    opcoes?.length ? ids.filter((id) => opcoes.some((o) => o.id === id)) : ids;

  // useMemo aqui não bastava: a dependência é o CATÁLOGO (`whatsApps`,
  // `etiquetas`), e ele troca de identidade a cada evento de sessão do
  // WhatsApp — inclusive na primeira carga, que sempre chega depois do
  // primeiro render. Cada troca fazia o resultado do filtro ser um array novo
  // com o MESMO conteúdo, o TicketsList zerava a lista e buscava tudo de
  // novo: a tela piscava e a ordem das conversas mudava sozinha no meio da
  // leitura. Estabilizar pelo conteúdo é o que corta esse ciclo.
  const tagsAtivas = useListaEstavel(soConhecidos(filtroTags, etiquetas));
  const usuariosAtivos = useListaEstavel(
    catalogoDeUsuariosParcial
      ? filtroUsuarios
      : soConhecidos(filtroUsuarios, usuarios),
  );
  const conexoesAtivas = useListaEstavel(soConhecidos(filtroConexoes, whatsApps));

  // O chip do filtro precisa do NOME mesmo com a gaveta fechada — e o
  // atendente escolhido pode ter ficado fora da primeira página de /users
  // (20 em 20). Sem isto o rótulo caía no cru "#34". Busca só os ids que
  // faltam, uma vez cada (o Set evita martelar um id que respondeu erro).
  const usuariosBuscadosRef = useRef(new Set());

  useEffect(() => {
    const faltam = usuariosAtivos.filter(
      (id) =>
        !usuarios.some((u) => u.id === id) &&
        !usuariosBuscadosRef.current.has(id),
    );

    if (!faltam.length) return;

    faltam.forEach((id) => usuariosBuscadosRef.current.add(id));

    (async () => {
      const achados = (
        await Promise.all(
          faltam.map((id) =>
            api
              .get(`/users/${id}`)
              .then((r) => r.data)
              .catch(() => null),
          ),
        )
      ).filter((u) => u?.id);

      if (achados.length) {
        setUsuarios((atuais) => [
          ...atuais,
          ...achados.filter((a) => !atuais.some((u) => u.id === a.id)),
        ]);
      }
    })();
  }, [usuariosAtivos, usuarios]);

  // Os filtros da gaveta ficam guardados por usuário — quem cobre sempre a
  // mesma etiqueta não deve remontar o recorte a cada login. O problema nunca
  // foi persistir; foi persistir INVISÍVEL: com a gaveta fechada, nada na tela
  // dizia que havia filtro, e lista curta por filtro esquecido parece lista
  // vazia. Daí os chips, a contagem no botão da gaveta e o "Limpar tudo".
  //
  // Aba, ordem, favoritas, pausa e período ficam de fora do "limpar":
  // são estados que a pessoa vê e desliga em outro lugar da barra.
  const chipsDeFiltro = useMemo(() => {
    const nome = (lista, id) =>
      (lista || []).find((o) => o.id === id)?.name || `#${id}`;

    const cor = (lista, id) => (lista || []).find((o) => o.id === id)?.color;

    return [
      ...(semFilaMarcada
        ? [
            {
              chave: "fila-sem",
              rotulo: "Sem departamento",
              remover: () => escolherFilas(filasMarcadas),
            },
          ]
        : []),
      ...filasMarcadas.map((id) => ({
        chave: `fila-${id}`,
        rotulo: nome(filasDisponiveis, id),
        cor: cor(filasDisponiveis, id),
        remover: () =>
          escolherFilas(filasNoFiltro.filter((f) => f !== id)),
      })),
      ...tagsAtivas.map((id) => ({
        chave: `tag-${id}`,
        rotulo: nome(etiquetas, id),
        cor: cor(etiquetas, id),
        remover: () => setFiltroTags(tagsAtivas.filter((t) => t !== id)),
      })),
      ...conexoesAtivas.map((id) => ({
        chave: `conexao-${id}`,
        rotulo: nome(whatsApps, id),
        remover: () =>
          setFiltroConexoes(conexoesAtivas.filter((c) => c !== id)),
      })),
      ...usuariosAtivos.map((id) => ({
        chave: `usuario-${id}`,
        rotulo: nome(usuarios, id),
        remover: () =>
          setFiltroUsuarios(usuariosAtivos.filter((u) => u !== id)),
      })),
      ...(filtroMotivos || []).map((chave) => ({
        chave: `motivo-${chave}`,
        rotulo:
          motivosParaFiltro.find((m) => m.id === chave)?.name || chave,
        remover: () =>
          setFiltroMotivos(filtroMotivos.filter((m) => m !== chave)),
      })),
    ];
  }, [
    filasMarcadas,
    semFilaMarcada,
    filasNoFiltro,
    filasDisponiveis,
    escolherFilas,
    tagsAtivas,
    conexoesAtivas,
    usuariosAtivos,
    filtroMotivos,
    etiquetas,
    whatsApps,
    usuarios,
    motivosParaFiltro,
    setFiltroTags,
    setFiltroConexoes,
    setFiltroUsuarios,
    setFiltroMotivos,
  ]);

  // Recortes que continuam valendo na busca além dos chips da gaveta.
  const recortesDaBusca = [
    ...(soFavoritas
      ? [
          {
            chave: "favoritas",
            rotulo: "só favoritas",
            remover: () => setSoFavoritas(false),
          },
        ]
      : []),
  ];

  const limparFiltros = useCallback(() => {
    escolherFilas([]);
    setFiltroTags([]);
    setFiltroConexoes([]);
    setFiltroUsuarios([]);
    setFiltroMotivos([]);
  }, [
    escolherFilas,
    setFiltroTags,
    setFiltroConexoes,
    setFiltroUsuarios,
    setFiltroMotivos,
  ]);

  // Encerrar e transferir em massa moram no mesmo lugar da barra porque são a
  // mesma ideia: agir sobre a lista inteira em vez de um a um. Quem pode usar
  // cada uma é o administrador que decide, cargo por cargo — o backend barra
  // de verdade, isto aqui só evita oferecer o que vai dar 403.
  // `null` fechado; "todos" veio da barra de ferramentas (a lista inteira);
  // "selecao" veio da barra de seleção. Com seleção ativa, os dois caminhos
  // existem ao mesmo tempo — um booleano faria o botão "Resolver todos"
  // encerrar só o que estivesse marcado, sem avisar.
  const [fecharTodos, setFecharTodos] = useState(null);
  const [transferirTodos, setTransferirTodos] = useState(null);

  // Seleção múltipla. Guarda id e situação, não o ticket inteiro: a barra
  // precisa do status para não oferecer "Resolver" numa seleção só de
  // conversas já encerradas, e nada além disso.
  const [selecionados, setSelecionados] = useState([]);
  const idsSelecionados = useMemo(
    () => selecionados.map((t) => t.id),
    [selecionados],
  );

  const alternarSelecao = useCallback((ticket) => {
    setSelecionados((atual) =>
      atual.some((t) => t.id === ticket.id)
        ? atual.filter((t) => t.id !== ticket.id)
        : [...atual, { id: ticket.id, status: ticket.status }],
    );
  }, []);

  const limparSelecao = useCallback(() => setSelecionados([]), []);

  // Cada atendimento pode aparecer duas vezes no DOM (a linha e a cópia que
  // sai da tela na animação): o Map por id resolve sem depender do desenho.
  // Vem do DOM pelo mesmo motivo do atalho j/k: é a única ordem que já tem
  // aba, filtro e busca aplicados.
  const visiveisAgora = () => {
    const porId = new Map();

    linhasDaListaVisivel().forEach((linha) => {
      const id = Number(linha.dataset.ticketId);

      if (id && !porId.has(id)) {
        porId.set(id, { id, status: linha.dataset.ticketStatus });
      }
    });

    return [...porId.values()];
  };

  const totalVisivel = visiveisAgora().length;

  const selecionarTodosVisiveis = () => {
    setSelecionados((atual) => {
      const jaTem = new Set(atual.map((t) => t.id));

      return [...atual, ...visiveisAgora().filter((t) => !jaTem.has(t.id))];
    });
  };

  // A seleção é da aba em que foi feita: mudar de aba e agir sobre o que não
  // está mais na tela é o tipo de engano que não dá para desfazer. Vale também
  // para a busca: entrar em "Procurar em todas" esconde a lista onde a seleção
  // foi feita, e outro termo ou filtro troca as linhas à vista.
  useEffect(() => {
    limparSelecao();
  }, [estado, painel, buscando, termoMensagem, filtro, buscaStatus, limparSelecao]);

  // Sem temporizador aqui: o `let` era recriado a cada render, então o
  // `clearTimeout` nunca cancelava nada — cada tecla agendava a sua própria
  // atualização e só somava meio segundo de atraso. Quem espera a digitação
  // parar é o `useTickets`, que já segura 500 ms antes de ir ao servidor.
  const mudarFiltroContato = (valor) => {
    setFiltroContato(valor);
    setContagemDaBusca(null);
  };

  const mudarBuscaMensagem = (valor) => {
    setBuscaMensagem(valor);
    setContagemDaBusca(null);
  };

  // As abas abertas só ficam montadas quando a tela precisa delas (ver o
  // render). Desmontadas, as contagens congelam — e não podem congelar no
  // número de um filtro que a pessoa já apagou: volta a última sem filtro.
  const abertasMontadas = (!buscando && painel === "abertos") || Boolean(filtro);
  const contagensSemFiltro = useRef(contagens);
  const atualizarContagem = (chave) => (valor) => {
    if (!filtro && !termoNaAba) {
      contagensSemFiltro.current = {
        ...contagensSemFiltro.current,
        [chave]: valor,
      };
    }
    setContagens((anterior) =>
      anterior[chave] === valor ? anterior : { ...anterior, [chave]: valor },
    );
  };
  useEffect(() => {
    if (!abertasMontadas) setContagens(contagensSemFiltro.current);
  }, [abertasMontadas]);

  // Onde a pessoa está. Resolvidos continua sendo o painel "fechados" por
  // baixo — período, motivo e contagem de pausa já falam com ele —; virar aba
  // (`resolvidosComoAba`) muda só como se chega lá.
  const estadoAtual =
    painel === "fechados"
      ? RESOLVIDOS
      : ESTADOS.find((e) => e.value === estado) || ESTADOS[0];
  const irPara = (valor) => {
    if (valor === RESOLVIDOS.value) {
      setPainel("fechados");
      return;
    }

    setPainel("abertos");
    setEstado(valor);
  };
  // No trilho Resolvidos é sempre pasta: lá não há onde pôr o par
  // Abertos/Fechados.
  const resolvidosNaNavegacao = resolvidosComoAba || lateral;
  const itensDaNavegacao = resolvidosNaNavegacao ? [...abas, RESOLVIDOS] : abas;

  // Aba vazia com filtro aponta onde a pessoa está. As contagens são as que
  // as listas já reportam; Resolvidos não conta, então não entra aqui.
  const vazioDaBusca = (item) => {
    const outras = abas.filter(
      (o) => o.value !== item.value && parseInt(contagens[o.value], 10) > 0,
    );

    return {
      titulo: termoNaAba
        ? `Nenhuma mensagem com “${termoNaAba}”${
            filtro ? ` de “${filtro}”` : ""
          } em ${item.rotulo}`
        : `Ninguém com “${filtro}” em ${item.rotulo}`,
      texto: outras.length ? "Encontrado em:" : "Nem nas outras abas abertas.",
      noTopo: true,
      extra: outras.length > 0 && (
        <div className="mt-2 flex flex-wrap justify-center gap-1.5">
          {outras.map((o) => (
            <Button
              key={o.value}
              variant="outline"
              size="sm"
              onClick={() => irPara(o.value)}
            >
              {o.rotulo} ({contagens[o.value]})
            </Button>
          ))}
        </div>
      ),
    };
  };

  // O conteúdo das visões e dos departamentos serve à barra e ao menu ⋯.
  const conteudoDasVisoes = (
    <>
      {visoes.length > 0 ? (
        <div className="mb-2 space-y-0.5">
          {visoes.map((v) => (
            <div
              key={v.id}
              className={cn(
                "flex items-center gap-1 rounded-md",
                visaoEmUso?.id === v.id && "bg-accent",
              )}
            >
              <button
                type="button"
                onClick={() => aplicarVisao(v)}
                className="flex min-w-0 flex-1 items-center gap-2 rounded-md px-2 py-1.5 text-left text-sm transition-colors hover:bg-accent"
              >
                <Check
                  className={cn(
                    "h-4 w-4 shrink-0",
                    visaoEmUso?.id === v.id ? "opacity-100" : "opacity-0",
                  )}
                />
                <span className="truncate">{v.nome}</span>
              </button>
              <button
                type="button"
                aria-label={`Apagar visão ${v.nome}`}
                title="Apagar"
                onClick={() => apagarVisao(v.id)}
                className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-destructive/10 hover:text-destructive"
              >
                <Trash2 className="h-3.5 w-3.5" />
              </button>
            </div>
          ))}
        </div>
      ) : (
        <p className="mb-2 px-2 py-1.5 text-xs text-muted-foreground">
          Nenhuma visão salva. Deixe a lista do jeito que você usa —
          aba, ordem, filtros e departamentos — e guarde aqui com um nome.
        </p>
      )}

      <div className="flex items-center gap-1.5 border-t pt-2">
        <Input
          value={nomeDaVisao}
          maxLength={40}
          placeholder="Nome da visão"
          onChange={(e) => setNomeDaVisao(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && salvarVisao()}
          className="h-8 text-xs"
        />
        <Button
          size="sm"
          className="h-8 shrink-0"
          disabled={!nomeDaVisao.trim()}
          onClick={salvarVisao}
        >
          Salvar
        </Button>
      </div>
    </>
  );
  const itensDoMenu = [
    noMenu("todos") &&
      podeVerTodos && {
        id: "todos",
        rotulo: "Ver os de todo mundo",
        icone: showAllTickets ? Eye : EyeOff,
        ativo: showAllTickets,
        aoClicar: () => setShowAllTickets((v) => !v),
      },
    noMenu("novo") && {
      id: "novo",
      rotulo: "Chamar um contato",
      icone: UserRound,
      aoClicar: () => setNovoAtendimento("lista"),
    },
    noMenu("novo") && {
      id: "novoContato",
      rotulo: "Adicionar contato",
      icone: UserPlus,
      aoClicar: () => setNovoAtendimento("novoContato"),
    },
    noMenu("favoritas") && {
      id: "favoritas",
      rotulo: totalFavoritas > 0 ? `Favoritas (${totalFavoritas})` : "Favoritas",
      icone: Star,
      ativo: soFavoritas,
      aoClicar: () => setSoFavoritas((v) => !v),
    },
    noMenu("adiados") && {
      id: "adiados",
      rotulo: pausados.total > 0 ? `Pausa (${pausados.total})` : "Pausa",
      icone: AlarmClock,
      ativo: soAdiados,
      aoClicar: () => setSoAdiados((v) => !v),
    },
    noMenu("resolverTodos") &&
      temPermissao("tickets:closeAll") && {
        id: "resolverTodos",
        rotulo: "Resolver todos",
        icone: ListChecks,
        aoClicar: () => setFecharTodos("todos"),
      },
    noMenu("transferirTodos") &&
      temPermissao("tickets:transferAll") && {
        id: "transferirTodos",
        rotulo: "Transferir todos",
        icone: UsersRound,
        aoClicar: () => setTransferirTodos("todos"),
      },
    noMenu("ordem") && {
      id: "ordem",
      rotulo: "Ordenar A–Z",
      icone: ArrowDownAZ,
      ativo: ordemAZ,
      aoClicar: () => setOrdemAZ((v) => !v),
    },
  ].filter(Boolean);
  const secoesDoMenu = [
    noMenu("visoes") && {
      id: "visoes",
      titulo: visaoEmUso ? `Visões salvas · ${visaoEmUso.nome}` : "Visões salvas",
      ativo: Boolean(visaoEmUso),
      conteudo: conteudoDasVisoes,
    },
  ].filter(Boolean);

  // Barra de ferramentas: cada ícone age sobre a lista inteira.
  // Quebra de linha (o mesmo recurso da faixa de chips logo abaixo)
  // porque a barra cresce com o perfil e com cada ferramenta nova: em
  // linha única o que não coubesse — o chip "Departamentos" era o último —
  // ficava fora da área visível, sem clique e sem rolagem. Enquanto
  // couber tudo numa linha o desenho não muda; o `ml-auto` continua
  // jogando os chips para a direita.
  // Barra curta (até uma ferramenta além de Personalizar e ⋯) não ganha
  // fileira própria: vai na fileira do seletor ou na do filtro.
  const barraCurta =
    ["todos", "novo", "favoritas", "adiados", "ordem", "visoes"].filter(
      (id) => naBarra(id) && (id !== "todos" || podeVerTodos),
    ).length +
      (podeResolverTodos || podeTransferirTodos ? 1 : 0) +
      (resolvidosNaNavegacao ? 0 : 2) <=
    1;
  const barraDeFerramentas = (
    <div
      className={cn(
        "flex shrink-0 flex-wrap items-center gap-x-0.5 gap-y-1",
        !barraCurta && "px-3 pb-1.5",
      )}
    >
      {naBarra("todos") && (
        <Can
          role={user.profile}
          perform="tickets-manager:showall"
          yes={() => (
            <FerramentaDaLista
              icone={showAllTickets ? Eye : EyeOff}
              rotulo="Todos"
              ativo={showAllTickets}
              onClick={() => setShowAllTickets((v) => !v)}
            />
          )}
        />
      )}

      {/* Novo atendimento: chamar quem já é contato e cadastrar alguém de
          fora são dois caminhos diferentes, e o segundo estava escondido
          atrás de digitar três letras que não achassem ninguém. */}
      {naBarra("novo") && (
        <Popover>
          <PopoverTrigger asChild>
            <div className="group/ferramenta relative">
              <button
                type="button"
                aria-label="Novo atendimento"
                className="flex h-8 w-8 items-center justify-center rounded-full bg-primary text-primary-foreground transition-colors hover:bg-primary/90"
              >
                <Plus className="h-[18px] w-[18px]" />
              </button>
              <span className="pointer-events-none absolute left-1/2 top-full z-20 mt-0.5 -translate-x-1/2 whitespace-nowrap rounded bg-foreground px-1.5 py-0.5 text-[10px] font-medium text-background opacity-0 transition-opacity group-hover/ferramenta:opacity-100">
                Novo
              </span>
            </div>
          </PopoverTrigger>
          <PopoverContent align="start" className="w-60 p-1">
            <button
              type="button"
              onClick={() => setNovoAtendimento("lista")}
              className="flex w-full items-start gap-2 rounded-md px-2 py-2 text-left text-sm transition-colors hover:bg-accent"
            >
              <UserRound className="mt-0.5 h-4 w-4 shrink-0" />
              <span className="min-w-0">
                <span className="block font-medium">Chamar um contato</span>
                <span className="block text-xs text-muted-foreground">
                  Buscar na lista de contatos
                </span>
              </span>
            </button>
            <button
              type="button"
              onClick={() => setNovoAtendimento("novoContato")}
              className="flex w-full items-start gap-2 rounded-md px-2 py-2 text-left text-sm transition-colors hover:bg-accent"
            >
              <UserPlus className="mt-0.5 h-4 w-4 shrink-0" />
              <span className="min-w-0">
                <span className="block font-medium">Adicionar contato</span>
                <span className="block text-xs text-muted-foreground">
                  Cadastrar um número novo e já abrir a conversa
                </span>
              </span>
            </button>
          </PopoverContent>
        </Popover>
      )}

      {naBarra("favoritas") && (
        <FerramentaDaLista
          icone={Star}
          rotulo="Favoritas"
          ativo={soFavoritas}
          classe="text-amber-500"
          dica={
            totalFavoritas > 0
              ? `Favoritas: ${totalFavoritas} marcada${totalFavoritas > 1 ? "s" : ""}`
              : "Favoritas"
          }
          contagem={totalFavoritas}
          preenchido={totalFavoritas > 0}
          onClick={() => setSoFavoritas((v) => !v)}
        />
      )}

      {naBarra("adiados") && (
        <FerramentaDaLista
          icone={AlarmClock}
          rotulo="Pausa"
          dica={[
            `Pausa: ${pausados.total} em pausa`,
            pausados.vencidos > 0 && `${pausados.vencidos} com o prazo vencido`,
            pausados.comMensagem > 0 &&
              `${pausados.comMensagem} com mensagem por responder`,
          ]
            .filter(Boolean)
            .join(", ")}
          ativo={soAdiados}
          // Três cores, os três estados do relógio da linha: vermelho para o
          // prazo vencido, âmbar para o cliente que escreveu antes da hora,
          // azul para a espera silenciosa. Vencido manda — é o que cobra
          // retorno prometido.
          classe={
            pausados.vencidos > 0
              ? "text-rose-600 dark:text-rose-400"
              : pausados.comMensagem > 0
                ? "text-amber-600 dark:text-amber-400"
                : "text-sky-600 dark:text-sky-400"
          }
          contagem={pausados.total}
          pulsando={pausados.vencidos > 0 && avisoPausaPiscar}
          onClick={() => setSoAdiados((v) => !v)}
        />
      )}

      {/* Agir sobre a lista inteira é uma ideia só: encerrar e transferir
          moram no mesmo botão. Cada item continua com a permissão dele. */}
      {(podeResolverTodos || podeTransferirTodos) && (
        <Popover>
          <PopoverTrigger asChild>
            <div className="group/ferramenta relative">
              <button
                type="button"
                aria-label="Ações em massa"
                className="flex h-8 w-8 items-center justify-center rounded-full text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
              >
                <ListChecks className="h-[18px] w-[18px]" />
              </button>
              <span className="pointer-events-none absolute left-1/2 top-full z-20 mt-0.5 -translate-x-1/2 whitespace-nowrap rounded bg-foreground px-1.5 py-0.5 text-[10px] font-medium text-background opacity-0 transition-opacity group-hover/ferramenta:opacity-100">
                Ações em massa
              </span>
            </div>
          </PopoverTrigger>
          <PopoverContent align="start" className="w-60 p-1">
            {podeResolverTodos && (
              <button
                type="button"
                onClick={() => setFecharTodos("todos")}
                className="flex w-full items-center gap-2 rounded-md px-2 py-2 text-left text-sm transition-colors hover:bg-accent"
              >
                <ListChecks className="h-4 w-4 shrink-0 text-emerald-600 dark:text-emerald-400" />
                Resolver todos
              </button>
            )}
            {podeTransferirTodos && (
              <button
                type="button"
                onClick={() => setTransferirTodos("todos")}
                className="flex w-full items-center gap-2 rounded-md px-2 py-2 text-left text-sm transition-colors hover:bg-accent"
              >
                <UsersRound className="h-4 w-4 shrink-0 text-sky-600 dark:text-sky-400" />
                Transferir todos
              </button>
            )}
          </PopoverContent>
        </Popover>
      )}

      {!resolvidosNaNavegacao && (
        <>
          <FerramentaDaLista
            icone={Inbox}
            rotulo="Abertos"
            ativo={painel === "abertos"}
            onClick={() => setPainel("abertos")}
          />

          <FerramentaDaLista
            icone={CheckSquare}
            rotulo="Fechados"
            ativo={painel === "fechados"}
            onClick={() => setPainel("fechados")}
          />
        </>
      )}

      {naBarra("ordem") && (
        <FerramentaDaLista
          icone={ordemAZ ? ArrowDownAZ : ArrowDownWideNarrow}
          // "Crescente/Decrescente" dizia ordem de tempo; o que o botão faz
          // é alternar entre nome e mais recente.
          rotulo={ordemAZ ? "A–Z" : "Recentes"}
          ativo={ordemAZ}
          onClick={() => setOrdemAZ((v) => !v)}
        />
      )}

      <PersonalizarLista>
        <button
          type="button"
          aria-label="Personalizar lista"
          title="Personalizar lista"
          className="flex h-8 w-8 items-center justify-center rounded-full text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
        >
          <SlidersHorizontal className="h-4 w-4" />
        </button>
      </PersonalizarLista>

      <MenuDaLista itens={itensDoMenu} secoes={secoesDoMenu} />

      <div className="ml-auto flex flex-wrap items-center justify-end gap-1.5">
        {naBarra("visoes") && (
          <Popover>
            <PopoverTrigger asChild>
              <button
                type="button"
                className={cn(
                  "flex h-7 items-center gap-1.5 rounded-full border px-2.5 text-xs font-medium transition-colors hover:bg-muted",
                  visaoEmUso
                    ? "border-primary/40 bg-primary/10 text-primary"
                    : "text-muted-foreground hover:text-foreground",
                )}
              >
                <Bookmark className="h-3.5 w-3.5" />
                <span className="max-w-24 truncate">
                  {visaoEmUso ? visaoEmUso.nome : "Visões"}
                </span>
              </button>
            </PopoverTrigger>
            <PopoverContent align="end" className="w-72 p-2">
              {conteudoDasVisoes}
            </PopoverContent>
          </Popover>
        )}

      </div>
    </div>
  );

  return (
    <div
      className={cn(
        "relative flex h-full flex-col overflow-hidden bg-background",
        // Recuo do trilho: as larguras são as do TrilhoDaLista.
        lateral && "pl-12 @[30rem]:pl-40",
      )}
    >
      <NewTicketModal
        modalOpen={Boolean(novoAtendimento)}
        novoContato={novoAtendimento === "novoContato"}
        onClose={() => setNovoAtendimento(null)}
      />

      {lateral && (
        <TrilhoDaLista
          itens={itensDaNavegacao}
          atual={estadoAtual.value}
          contagens={contagens}
          aoIr={irPara}
          pastas={[
            {
              value: "favoritas",
              rotulo: "Favoritas",
              icone: Star,
              ativo: soFavoritas,
              contagem: totalFavoritas,
              onClick: () => setSoFavoritas((v) => !v),
            },
            {
              value: "pausa",
              rotulo: "Em pausa",
              icone: AlarmClock,
              ativo: soAdiados,
              contagem: pausados.total,
              alerta: pausados.vencidos > 0 && avisoPausaPiscar,
              onClick: () => setSoAdiados((v) => !v),
            },
            // "Oculto" no Personalizar lista vale também para a pasta.
          ].filter((pasta) =>
            temFerramenta(pasta.value === "pausa" ? "adiados" : pasta.value),
          )}
        />
      )}

      {seletor && (
        <SeletorDaLista
          itens={itensDaNavegacao}
          atual={estadoAtual}
          contagens={contagens}
          aoIr={irPara}
        >
          {barraCurta && barraDeFerramentas}
        </SeletorDaLista>
      )}

      {/* Filtro de contato da aba e busca nas mensagens. Esc ou o X limpam. */}
      <div className="flex shrink-0 items-center gap-2 px-3 pb-1.5 pt-3">
        <CampoDeBusca
          icone={Search}
          inputRef={searchInputRef}
          data-busca-da-lista=""
          valor={filtroContato}
          aoMudar={mudarFiltroContato}
          // Curto porque a coluna é estreita; o texto inteiro vai no rótulo.
          // Na busca em todas o filtro vale para o resultado, não para a aba.
          placeholder={`Filtrar ${buscando ? "resultado" : estadoAtual.rotulo}…`}
          rotulo={`Filtrar ${
            buscando ? "resultado" : estadoAtual.rotulo
          } por nome ou telefone`}
          rotuloLimpar="Limpar filtro"
        />

        {campoBuscaMensagem === "icone" && (
          <button
            type="button"
            aria-label="Buscar nas mensagens"
            title="Buscar nas mensagens"
            aria-pressed={campoDaMensagemAberto}
            onClick={() => {
              if (campoDaMensagemAberto) mudarBuscaMensagem("");
              setBuscaMensagemAberta(!campoDaMensagemAberto);
            }}
            className={cn(
              "flex h-9 w-9 shrink-0 items-center justify-center rounded-full transition-colors",
              campoDaMensagemAberto
                ? "bg-primary/10 text-primary"
                : "text-muted-foreground hover:bg-muted hover:text-foreground",
            )}
          >
            <MessageSquareText className="h-4 w-4" />
          </button>
        )}

        <button
          type="button"
          aria-label={filtrosAbertos ? "Esconder filtros" : "Mostrar filtros"}
          title={filtrosAbertos ? "Esconder filtros" : "Mostrar filtros"}
          aria-pressed={filtrosAbertos}
          onClick={() => setFiltrosAbertos((v) => !v)}
          className={cn(
            "relative flex h-9 w-9 shrink-0 items-center justify-center rounded-full transition-colors",
            filtrosAbertos
              ? "bg-primary/10 text-primary"
              : "text-muted-foreground hover:bg-muted hover:text-foreground",
          )}
        >
          {filtrosAbertos ? (
            <Filter className="h-4 w-4" />
          ) : (
            <FilterX className="h-4 w-4" />
          )}
          {/* Gaveta fechada não pode esconder que há filtro ligado. */}
          {!filtrosAbertos && chipsDeFiltro.length > 0 && (
            <span className="absolute -right-0.5 -top-0.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-primary px-1 text-[9px] font-bold tabular-nums text-primary-foreground">
              {chipsDeFiltro.length > 99 ? "99+" : chipsDeFiltro.length}
            </span>
          )}
        </button>

        {barraCurta && !seletor && barraDeFerramentas}
      </div>

      {(campoBuscaMensagem !== "icone" || campoDaMensagemAberto) && (
        <div className="flex shrink-0 px-3 pb-1.5">
          <CampoDeBusca
            icone={MessageSquareText}
            valor={buscaMensagem}
            aoMudar={mudarBuscaMensagem}
            autoFocus={campoBuscaMensagem === "icone"}
            rotuloLimpar="Limpar busca nas mensagens"
            placeholder={
              escopoBuscaMensagem === "todas"
                ? "Buscar em todas as mensagens"
                : `Buscar nas mensagens de ${estadoAtual.rotulo}`
            }
          />
        </div>
      )}

      {filtrosAbertos && (
        <div
          ref={refDaGaveta}
          className="flex shrink-0 flex-wrap gap-1.5 border-b px-3 pb-3"
        >
          <FiltroDeLista
            rotulo="Departamentos"
            opcoes={opcoesDeFilas}
            selecionados={filasNoFiltro}
            onChange={escolherFilas}
            aberto={filtroAberto === "departamentos"}
            aoAlternar={(abrir) =>
              setFiltroAberto(abrir ? "departamentos" : null)
            }
            refDaGaveta={refDaGaveta}
          />
          <FiltroDeLista
            rotulo="Etiquetas"
            opcoes={etiquetas}
            selecionados={tagsAtivas}
            onChange={setFiltroTags}
            erro={erroDasOpcoes}
            aoTentarDeNovo={recarregarOpcoes}
            aberto={filtroAberto === "etiquetas"}
            aoAlternar={(abrir) => setFiltroAberto(abrir ? "etiquetas" : null)}
            refDaGaveta={refDaGaveta}
          />
          <FiltroDeLista
            rotulo="Conexões"
            opcoes={whatsApps}
            selecionados={conexoesAtivas}
            onChange={setFiltroConexoes}
            aberto={filtroAberto === "conexoes"}
            aoAlternar={(abrir) => setFiltroAberto(abrir ? "conexoes" : null)}
            refDaGaveta={refDaGaveta}
          />
          {painel === "fechados" && (
            <FiltroDeLista
              rotulo="Motivo do encerramento"
              opcoes={motivosParaFiltro}
              selecionados={filtroMotivos}
              onChange={setFiltroMotivos}
              aberto={filtroAberto === "motivos"}
              aoAlternar={(abrir) => setFiltroAberto(abrir ? "motivos" : null)}
            refDaGaveta={refDaGaveta}
            />
          )}
          <Can
            role={user.profile}
            perform="tickets-manager:showall"
            yes={() => (
              <FiltroDeLista
                rotulo="Atendentes"
                opcoes={usuarios}
                selecionados={usuariosAtivos}
                onChange={setFiltroUsuarios}
                erro={erroDasOpcoes}
                aoTentarDeNovo={recarregarOpcoes}
                aberto={filtroAberto === "atendentes"}
                aoAlternar={(abrir) =>
                  setFiltroAberto(abrir ? "atendentes" : null)
                }
                refDaGaveta={refDaGaveta}
              />
            )}
          />
        </div>
      )}

      {!barraCurta && barraDeFerramentas}

      {/* Filtro ligado tem que estar à vista, com a gaveta aberta ou fechada:
          é o que faltava para lista curta por filtro esquecido não parecer
          lista vazia. Cada chip remove só a si; "Limpar tudo" zera os quatro
          filtros da gaveta, e nada além deles. */}
      {chipsDeFiltro.length > 0 && (
        // Teto de altura: as preferências aceitam dezenas de filtros, e sem
        // limite a faixa de chips empurrava a lista para altura zero.
        <div className="scrollbar-discreta flex max-h-16 shrink-0 flex-wrap gap-1 overflow-y-auto border-b px-3 pb-2">
          {chipsDeFiltro.map((chip) => (
            <span
              key={chip.chave}
              className="flex h-7 items-center gap-1.5 rounded-full border px-2.5 text-xs text-muted-foreground"
            >
              {chip.cor && (
                <span
                  className="h-2.5 w-2.5 shrink-0 rounded-full"
                  style={{ backgroundColor: chip.cor }}
                />
              )}
              <span className="max-w-32 truncate">{chip.rotulo}</span>
              <button
                type="button"
                aria-label={`Remover filtro ${chip.rotulo}`}
                title="Remover"
                onClick={chip.remover}
                className="text-muted-foreground transition-colors hover:text-destructive"
              >
                <X className="h-3 w-3" />
              </button>
            </span>
          ))}

          <button
            type="button"
            onClick={limparFiltros}
            className="flex h-7 items-center rounded-full border px-2.5 text-xs text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
          >
            Limpar tudo
          </button>
        </div>
      )}

      {/* Abas: ícone com a contagem em cima e o nome ao lado. Só existem no
          painel de abertos — resolvidos é o outro painel. */}
      <div
        className={cn(
          "shrink-0 border-b px-2",
          // Na busca em todas as mensagens as abas somem: o resultado procura
          // em todas elas, e a aba sublinhada diria o contrário. No filtro de
          // contato e na busca da aba elas ficam, contando quem bate.
          // Trilho e seletor substituem as abas.
          (lateral ||
            seletor ||
            buscando ||
            (painel !== "abertos" && !resolvidosComoAba)) &&
            "hidden",
        )}
      >
        <div
          role="tablist"
          aria-label="Recorte da lista: minhas, nossas (do departamento) ou aguardando"
          className="flex"
        >
          {itensDaNavegacao.map((item) => {
            const ativo = estadoAtual.value === item.value;
            const contagem = contagens[item.value];
            const Icone = item.icone;

            return (
              <button
                key={item.value}
                type="button"
                role="tab"
                aria-selected={ativo}
                title={item.ajuda}
                onClick={() => irPara(item.value)}
                className={cn(
                  // gap-3: com gap-2 o contador, que fica pendurado 8px à
                  // direita do ícone, encostava na primeira letra do rótulo —
                  // "3AGUARDANDO" numa tela de celular.
                  // px-1 (e não px-2): com quatro abas no celular cada uma
                  // fica com ~90px, e cada pixel de padding sai do rótulo.
                  "relative flex min-w-0 flex-1 items-center justify-center gap-3 px-1 py-2.5 text-[11px] font-semibold uppercase tracking-wide transition-colors",
                  ativo
                    ? "text-primary"
                    : "text-muted-foreground hover:text-foreground",
                  // Filtrando, aba sem ninguém fica apagada: a conta é dela.
                  (filtro || termoNaAba) &&
                    !ativo &&
                    !(parseInt(contagem, 10) > 0) &&
                    "opacity-50",
                )}
              >
                <span className="relative">
                  <Icone className="h-[18px] w-[18px]" />
                  {parseInt(contagem, 10) > 0 && (
                    <span className="absolute -right-2 -top-1.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-primary px-1 text-[9px] font-bold tabular-nums text-primary-foreground">
                      {/* Mesmo teto do selo das ferramentas: acima de 99 o
                          número estoura a bolinha. Vira "99+" tanto para o
                          total quanto para o piso ("120+"). */}
                      {parseInt(contagem, 10) > 99 ? "99+" : contagem}
                    </span>
                  )}
                </span>
                {/* Com Resolvidos como quinta aba, a coluna estreita não
                    cabe cinco nomes: fica só o ícone, como no trilho. */}
                <span
                  className={cn(
                    "truncate",
                    itensDaNavegacao.length > 4 &&
                      !ativo &&
                      "sr-only @[30rem]:not-sr-only",
                  )}
                >
                  {item.rotulo}
                </span>

                {ativo && (
                  <span className="absolute inset-x-0 bottom-0 h-0.5 bg-primary" />
                )}
              </button>
            );
          })}
        </div>
      </div>

      {/* Os mesmos dois modais servem a lista inteira e a seleção: o que muda
          é receberem ou não os ids. Duas versões da mesma pergunta
          divergiriam no primeiro ajuste. */}
      <ResolverTodosModal
        open={Boolean(fecharTodos)}
        onClose={() => setFecharTodos(null)}
        ticketIds={fecharTodos === "selecao" ? idsSelecionados : undefined}
        aoConcluir={limparSelecao}
      />

      <TransferirTodosModal
        open={Boolean(transferirTodos)}
        onClose={() => setTransferirTodos(null)}
        ticketIds={transferirTodos === "selecao" ? idsSelecionados : undefined}
        aoConcluir={limparSelecao}
      />

      <BarraDeSelecao
        quantidade={selecionados.length}
        total={totalVisivel}
        aoSelecionarTodos={selecionarTodosVisiveis}
        aoLimpar={limparSelecao}
        aoResolver={() => setFecharTodos("selecao")}
        aoTransferir={() => setTransferirTodos("selecao")}
        // Encerrar o que já está encerrado não é ação; transferir, sim — um
        // atendimento resolvido pode mudar de dono para quem for reabrir.
        podeResolver={
          temPermissao("tickets:closeAll") &&
          selecionados.some((t) => t.status !== "closed")
        }
        podeTransferir={temPermissao("tickets:transferAll")}
      />

      {/* Lista */}
      {termoMensagem && (
        <div
          className={cn(
            "flex items-center gap-1 border-t px-3 pt-2 text-xs text-muted-foreground",
            // Sem chips de recorte, a frase encolhe em vez de empurrar o botão
            // para a linha de baixo (aba de nome longo em 390px).
            buscando ? "flex-wrap" : "pb-2",
          )}
        >
          <span
            className={cn(!buscando && "min-w-0 truncate")}
            title={`Mensagens com “${termoMensagem}”`}
          >
            Mensagens com{" "}
            <span className="font-medium text-foreground">
              “{termoMensagem}”
            </span>{" "}
            {!buscando
              ? `em ${estadoAtual.rotulo}`
              : recortesDaBusca.length
                ? "só em:"
                : "em todas"}
          </span>
          {/* O que ainda estreita a busca fica à vista e sai com um clique:
              favoritas e departamentos valiam em silêncio, e a conversa que
              "não existia" estava só fora do recorte. */}
          {buscando &&
            recortesDaBusca.map((r) => (
              <span
                key={r.chave}
                className="flex h-6 items-center gap-1 rounded-full border px-2"
              >
                {r.rotulo}
                <button
                  type="button"
                  aria-label={`Buscar sem o filtro ${r.rotulo}`}
                  title="Tirar da busca"
                  onClick={r.remover}
                  className="hover:text-destructive"
                >
                  <X className="h-3 w-3" />
                </button>
              </span>
            ))}
          {/* O escopo é preferência, e muda daqui mesmo — salvo se o cargo
              o fixou. */}
          {!valorFixoPeloCargo("escopoBuscaMensagem") && (
            <button
              type="button"
              onClick={() =>
                setEscopoBuscaMensagem(buscando ? "aba" : "todas")
              }
              className="ml-auto whitespace-nowrap rounded-md px-1.5 py-0.5 font-medium text-primary hover:bg-primary/10"
            >
              {buscando ? `Só em ${estadoAtual.rotulo}` : "Procurar em todas"}
            </button>
          )}
        </div>
      )}
      {buscando && (
        <div className="flex flex-wrap items-center gap-1 gap-y-1 px-3 py-2">
          <span className="text-xs text-muted-foreground">Situação</span>
          {[
            { v: "", r: "Todas" },
            { v: "open", r: "Abertos" },
            { v: "pending", r: "Aguardando" },
            { v: "closed", r: "Resolvidos" },
          ].map((op) => {
            // "Todas" soma tudo, inclusive situações sem botão próprio.
            const total = contagemDaBusca
              ? op.v
                ? contagemDaBusca[op.v] || 0
                : Object.values(contagemDaBusca).reduce((a, b) => a + b, 0)
              : null;

            return (
              <button
                key={op.v || "todas"}
                type="button"
                onClick={() => setBuscaStatus(op.v)}
                className={cn(
                  "rounded-md px-2 py-1 text-xs transition-colors",
                  buscaStatus === op.v
                    ? "bg-primary text-primary-foreground"
                    : "text-muted-foreground hover:bg-muted",
                )}
              >
                {op.r}
                {total !== null && (
                  <span className="ml-1 tabular-nums opacity-70">{total}</span>
                )}
              </button>
            );
          })}
        </div>
      )}

      {/* Achou aqui e também em outras abas: sem isto, só a aba vazia
          contava ("Encontrado em"), e quem achou um resultado parava nele. */}
      {(filtro || termoNaAba) &&
        !buscando &&
        painel === "abertos" &&
        parseInt(contagens[estadoAtual.value], 10) > 0 &&
        (() => {
          const outras = abas.filter(
            (o) =>
              o.value !== estadoAtual.value &&
              parseInt(contagens[o.value], 10) > 0,
          );
          if (!outras.length) return null;
          return (
            <div className="flex flex-wrap items-center gap-1 border-t px-3 py-1.5 text-xs text-muted-foreground">
              <span>Também em:</span>
              {outras.map((o) => (
                <button
                  key={o.value}
                  type="button"
                  onClick={() => irPara(o.value)}
                  className="rounded-md px-1.5 py-0.5 font-medium text-primary hover:bg-primary/10"
                >
                  {o.rotulo} ({contagens[o.value]})
                </button>
              ))}
            </div>
          );
        })()}

      {/* Cabeçalho das colunas do layout Painel. Só ali: no Clássico e no Foco
          a linha é um cartão, e rótulo de coluna não descreveria nada.
          As larguras vêm de TicketListItem/colunasPainel.js, o mesmo lugar de
          onde a linha as lê. */}
      {layout === "painel" && (
        <div className="flex shrink-0 items-center gap-3 border-t bg-muted/40 px-3 py-1 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
          {/* Vaga da caixa de seleção: ela existe em toda linha (invisível até
              o hover), então sem este espaçador o cabeçalho fica deslocado das
              colunas que ele nomeia. Mesma largura da vaga na linha (w-6);
              com w-4 o cabeçalho inteiro ficava 8px à esquerda dos dados. */}
          <span className="w-6 shrink-0" aria-hidden="true" />
          <span className="w-[68px] shrink-0 text-right">Espera</span>
          <span className="w-4 shrink-0" aria-hidden="true" />
          <span className="min-w-[104px] flex-[1.2] truncate">Contato</span>
          {COLUNAS_PAINEL.filter((c) => colunasVisiveis.includes(c.id)).map(
            (c) => (
              <span key={c.id} className={c.classe}>
                {c.curto || c.rotulo}
              </span>
            ),
          )}
          <span className="w-14 shrink-0 text-right">Última</span>
        </div>
      )}

      <div className="relative min-h-0 flex-1 border-t">
        {/* Busca em todas: lista própria por cima. */}
        {buscando && (
          <div className="absolute inset-0 z-20 bg-background">
            <TicketsList
              filtroContato={filtro}
              buscaMensagem={termoMensagem}
              escopoBuscaMensagem="todas"
              status={buscaStatus || undefined}
              aoContarSituacoes={setContagemDaBusca}
              // Os filtros da barra valem na busca também: ligá-los e ver a
              // busca ignorá-los era metade da confusão.
              tagIds={tagsAtivas}
              userIds={usuariosAtivos}
              whatsappIds={conexoesAtivas}
              onlyFavorites={soFavoritas}
              ordemAZ={ordemAZ}
              mostrarStatus
              // Sem `showAll`: o alcance da busca é do servidor, que já procura
              // em tudo que o escopo do cargo permite ver. Mandar `true` daqui
              // era o navegador pedindo o alcance máximo em toda busca — hoje
              // inofensivo porque o escopo manda, e uma bomba armada para a
              // primeira mudança no backend.
              selectedQueueIds={selectedQueueIds}
              semFila={semFila}
              colunasVisiveis={colunasVisiveis}
              itensVisiveis={itensVisiveis}
              densidade={densidadeLinha}
              botoesVisiveis={botoesDaLinha}
              piscarPausa={avisoPausaPiscar}
              recorrenteMinimo={recorrenteMinimo}
              mostrarConexao={mostrarConexao}
              vazio={{
                titulo: `Nenhuma mensagem com “${termoMensagem}”${
                  filtro ? ` de “${filtro}”` : ""
                }`,
                texto: recortesDaBusca.length
                  ? `A busca está limitada a ${recortesDaBusca
                      .map((r) => r.rotulo)
                      .join(" e ")}. Tire o filtro acima para buscar em tudo.`
                  : "Tente outro trecho da conversa.",
                noTopo: true,
              }}
            />
          </div>
        )}
            {/* Recorte de período: fica só no painel de fechados, que é o que
                acumula. Nas abas de abertos não faz sentido — o que está
                aberto interessa independentemente da idade. */}
            {painel === "fechados" && (
              <div className="absolute inset-x-0 top-0 z-10 flex items-center gap-2 border-b bg-background px-3 py-2">
                <span className="text-xs text-muted-foreground">Mostrar</span>
                {[
                  { v: "7", r: "7 dias" },
                  { v: "30", r: "30 dias" },
                  { v: "90", r: "90 dias" },
                  { v: "todos", r: "Tudo" },
                ].map((op) => (
                  <button
                    key={op.v}
                    type="button"
                    onClick={() => setPeriodoResolvidos(op.v)}
                    className={cn(
                      "rounded-md px-2 py-1 text-xs transition-colors",
                      periodoResolvidos === op.v
                        ? "bg-primary text-primary-foreground"
                        : "text-muted-foreground hover:bg-muted",
                    )}
                  >
                    {op.r}
                  </button>
                ))}
              </div>
            )}
            {/* Só o que o estado visível precisa: cada lista montada é uma
                consulta a /tickets. Fora de Abertos e na busca em todas, as
                abas abertas só ficam (escondidas) com filtro de contato — é
                delas que sai o "Encontrado em". Sem elas, as contagens
                congelam, como sempre foi com Resolvidos aberto. */}
            {[
              ...(abertasMontadas ? abas : []),
              ...(!buscando && painel === "fechados" ? [RESOLVIDOS] : []),
            ].map((item) => (
              <div
                key={item.value}
                className={cn(
                  "absolute inset-0",
                  // A barra de período ocupa o topo: sem empurrar a lista, ela
                  // fica por cima do primeiro atendimento.
                  item.value === "closed" && "top-[41px]",
                  // `hidden` (display:none), não `invisible`
                  // (visibility:hidden): as quatro abas ficam montadas — é
                  // delas que saem os contadores dos ícones —, mas o que não
                  // está à vista não pode custar layout. Medido em produção em
                  // 16/09/2026: 6.847 nós no DOM da tela, 4.584 deles (67%)
                  // nas abas escondidas, recalculados a cada invalidação.
                  (buscando || estadoAtual.value !== item.value) && "hidden",
                )}
              >
                <TicketsList
                  filtroContato={filtro}
                  buscaMensagem={termoNaAba}
                  status={item.status}
                  visao={item.visao}
                  ultimosDias={
                    item.value === "closed" && periodoResolvidos !== "todos"
                      ? periodoResolvidos
                      : undefined
                  }
                  onlyFavorites={soFavoritas}
                  soAdiados={soAdiados}
                  showAll={item.value === "closed" ? true : showAllTickets}
                  ordemAZ={ordemAZ}
                  tagIds={tagsAtivas}
                  userIds={usuariosAtivos}
                  whatsappIds={conexoesAtivas}
                  closeReasons={
                    item.value === "closed" ? filtroMotivos : undefined
                  }
                  selecionados={idsSelecionados}
                  selecionando={selecionados.length > 0}
                  aoSelecionar={alternarSelecao}
                  colunasVisiveis={colunasVisiveis}
                  itensVisiveis={itensVisiveis}
                  densidade={densidadeLinha}
                  botoesVisiveis={botoesDaLinha}
                  piscarPausa={avisoPausaPiscar}
                  recorrenteMinimo={recorrenteMinimo}
                  mostrarConexao={mostrarConexao}
                  // Só para a lista devolver a rolagem ao reaparecer:
                  // `display:none` zera o `scrollTop`.
                  ativo={!buscando && estadoAtual.value === item.value}
                  selectedQueueIds={selectedQueueIds}
                  semFila={semFila}
                  isGroup={item.somenteGrupos ? "true" : "false"}
                  vazio={filtro || termoNaAba ? vazioDaBusca(item) : item.vazio}
                  // O motivo do encerramento só é aplicado em Resolvidos: contá-lo
                  // nas abas abertas faria a tela culpar um filtro que não está
                  // escondendo nada ali.
                  // Filtrando, o vazio é o da busca ("Encontrado em"); os chips
                  // da gaveta continuam à vista lá em cima.
                  filtrosAtivos={
                    filtro || termoNaAba
                      ? 0
                      : chipsDeFiltro.length -
                        (item.value === "closed"
                          ? 0
                          : (filtroMotivos || []).length)
                  }
                  aoLimparFiltros={limparFiltros}
                  updateCount={
                    item.value === "closed"
                      ? undefined
                      : atualizarContagem(item.value)
                  }
                />
              </div>
            ))}
      </div>
    </div>
  );
};

export default TicketsManager;
